import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, FlaskConical, Play } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button } from '../../ui';
import { useQuery } from '../../gql/hooks';
import {
  CANONICAL_MODELS_QUERY,
  SIMULATE_ROUTE_QUERY,
  type CanonicalModelsQueryResult,
  type SimulateRouteQueryResult,
  type SimulateRouteQueryVariables,
} from '../../gql/operations';
import { CanonicalModelKind, type Provider } from '../../gql/types';
import { InlineBanner } from '../shared/InlineBanner';
import { RouteSimulationResult } from './RouteSimulationResult';
import type { ApplicationOption } from './RoutingPolicyFormModal';

// "Test routing" simulator (W5). A collapsible panel that dry-runs the router for an (optional
// application, canonical model) pair via SIMULATE_ROUTE_QUERY and renders the outcome as the same
// chain visualization. Model is required; the application picker allows a "global (no app)" choice.

const GLOBAL = '__global__';

export interface RouteSimulatorPanelProps {
  applications: ApplicationOption[];
  /** Live providers — passed through for parity with the policy cards (unused directly here). */
  providers?: Provider[] | undefined;
}

export function RouteSimulatorPanel({ applications }: RouteSimulatorPanelProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [open, setOpen] = useState(false);
  const [appId, setAppId] = useState<string>(GLOBAL);
  const [model, setModel] = useState('');
  const [touched, setTouched] = useState(false);

  // The submitted variables drive the query; null until the first valid run so nothing fires on
  // mount. The query re-keys (and refetches) whenever these change.
  const [submitted, setSubmitted] = useState<SimulateRouteQueryVariables | null>(null);

  // Prepopulated canonical-model picker (2026-06-07): fetch enabled rows from the
  // catalog so the operator selects from a known-valid set instead of typing a name and
  // discovering at simulation time that the model doesn't exist. The picker also gets
  // an empty placeholder while the catalog loads — the simulator stays inert until a
  // pick lands so we don't fire `simulateRoute` against a `''` model.
  const canonicalModelsQuery = useQuery<CanonicalModelsQueryResult>(
    CANONICAL_MODELS_QUERY,
    { enabled: true },
  );

  const canonicalModelOptions = useMemo(() => {
    const all = canonicalModelsQuery.data?.canonicalModels ?? [];
    // Sort enabled first (the query already filters to enabled but we keep the
    // structural model identical to the provider form's picker for consistency), with
    // `auto` pinned to the top because it's the most-tested-first choice.
    const sorted = [...all].sort((a, b) => {
      if (a.kind === CanonicalModelKind.Auto && b.kind !== CanonicalModelKind.Auto) return -1;
      if (b.kind === CanonicalModelKind.Auto && a.kind !== CanonicalModelKind.Auto) return 1;
      return a.name.localeCompare(b.name);
    });
    return sorted.map((m) => ({
      value: m.name,
      label: m.displayName ? `${m.name}  ·  ${m.displayName}` : m.name,
    }));
  }, [canonicalModelsQuery.data]);

  // Seed the picker with the first option once the catalog loads so the operator can
  // click Run without picking anything. The auto-seeding only fires when the field is
  // genuinely empty so an explicit clear isn't immediately undone.
  useEffect(() => {
    if (model !== '') return;
    const first = canonicalModelOptions[0];
    if (first) setModel(first.value);
  }, [canonicalModelOptions, model]);

  const modelError =
    touched && model.trim() === '' ? 'Pick a canonical model.' : undefined;

  const appOptions = useMemo(
    () => [
      { value: GLOBAL, label: 'Global (no application scope)' },
      ...applications.map((a) => ({ value: a.id, label: a.name })),
    ],
    [applications],
  );

  const queryVars: SimulateRouteQueryVariables = {
    applicationId: submitted?.applicationId ?? null,
    canonicalModel: submitted?.canonicalModel ?? '',
  };

  const { data, loading, error } = useQuery<
    SimulateRouteQueryResult,
    SimulateRouteQueryVariables
  >(SIMULATE_ROUTE_QUERY, queryVars, { enabled: submitted !== null });

  const handleRun = () => {
    setTouched(true);
    const trimmedModel = model.trim();
    if (trimmedModel === '') return;
    setSubmitted({
      applicationId: appId === GLOBAL ? null : appId,
      canonicalModel: trimmedModel,
    });
  };

  return (
    <section
      aria-label="Route simulator"
      className={`mb-6 overflow-hidden rounded-2xl border ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
          isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'
        }`}
      >
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/20">
            <FlaskConical className="h-5 w-5" aria-hidden="true" />
          </span>
          <span>
            <span className={`block text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Test routing
            </span>
            <span className={`block text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              Dry-run the router for an application + model and see which provider wins.
            </span>
          </span>
        </span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          } ${isDark ? 'text-white/40' : 'text-slate-400'}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className={`space-y-4 border-t px-5 py-5 ${
            isDark ? 'border-white/[0.06]' : 'border-slate-100'
          }`}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <span
                className={`block text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}
              >
                Application
              </span>
              <AutocompleteSelect
                label="Simulate application"
                value={appId}
                options={appOptions}
                onChange={setAppId}
              />
            </div>
            <div className="space-y-1.5">
              <span
                className={`block text-xs font-medium ${
                  isDark ? 'text-white/60' : 'text-slate-600'
                }`}
              >
                Canonical model <span aria-hidden="true">*</span>
              </span>
              <AutocompleteSelect
                label="Canonical model"
                value={model}
                options={canonicalModelOptions}
                onChange={(next) => {
                  setModel(next);
                  setTouched(true);
                }}
                placeholder={
                  canonicalModelsQuery.loading
                    ? 'Loading models…'
                    : canonicalModelOptions.length === 0
                      ? 'No enabled canonical models'
                      : 'Pick a canonical model'
                }
              />
              {modelError && (
                <span
                  className={`block text-[11px] ${
                    isDark ? 'text-rose-300' : 'text-rose-600'
                  }`}
                >
                  {modelError}
                </span>
              )}
            </div>
          </div>

          <Button
            variant="primary"
            loading={loading}
            onClick={handleRun}
            leftIcon={<Play className="h-4 w-4" aria-hidden="true" />}
          >
            Run simulation
          </Button>

          {error && (
            <InlineBanner tone="error" testId="sim-error" className="mb-0">
              The simulation could not be run: {error.message}
            </InlineBanner>
          )}

          {submitted && !loading && !error && data?.simulateRoute && (
            <div className="pt-1">
              <RouteSimulationResult result={data.simulateRoute} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
